import React, { useState } from "react";
import axios from "axios";
import "./QuoteRequest.css";

const initialState = {
  // Client Information
  name: "",
  email: "",
  phone: "",
  address: "",
  
  // Job Advertisement Details
  jobTitle: "",
  jobLocation: "",
  jobDescription: "",
  qualifications: "",
  salary: "",
  employmentType: "",
  benefits: "",
  
  // Advertisement Requirements
  visaType: "",
  wageDetails: "",
  adPlacement: "",
  pubDates: "",
  pubRegions: "",
  specialInstructions: ""
};

export const QuoteRequest = () => {
  const [state, setState] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [currentStep, setCurrentStep] = useState(1);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState((prevState) => ({ ...prevState, [name]: value }));
  };

  const clearState = () => setState({ ...initialState });

  const nextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSuccessMessage("");
    setErrorMessage("");
    
    try {
      const apiEndpoint = "https://edisiuho2l.execute-api.us-east-1.amazonaws.com/prod/quote";
      
      const response = await axios.post(apiEndpoint, state);
      
      if (response.data.success) {
        clearState();
        setCurrentStep(1);
        setSuccessMessage("Quote request submitted successfully! We'll get back to you soon.");
      } else {
        setErrorMessage("Failed to submit quote request. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting quote request:", error);
      setErrorMessage("Failed to submit quote request. Please contact us directly via email.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render different form sections based on current step
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return renderClientInfo();
      case 2:
        return renderJobDetails();
      case 3:
        return renderAdRequirements();
      default:
        return renderClientInfo();
    }
  };

  const renderClientInfo = () => {
    return (
      <>
        <h3>Client Information</h3>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                className="form-control"
                placeholder="Your Name"
                required
                value={state.name}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                className="form-control"
                placeholder="Your Email"
                required
                value={state.email}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                className="form-control"
                placeholder="Your Phone Number"
                value={state.phone}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="address">Firm's Address</label>
              <input
                type="text"
                id="address"
                name="address"
                className="form-control"
                placeholder="Your Firm's Address"
                value={state.address}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
        <div className="text-right">
          <button 
            type="button" 
            className="btn btn-custom btn-lg"
            onClick={nextStep}
          >
            Next
          </button>
        </div>
      </>
    );
  };

  const renderJobDetails = () => {
    return (
      <>
        <h3>Job Advertisement Details</h3>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="jobTitle">Job Title</label>
              <input
                type="text"
                id="jobTitle"
                name="jobTitle"
                className="form-control"
                placeholder="Job Title"
                value={state.jobTitle}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="jobLocation">Job Location (City, State)</label>
              <input
                type="text"
                id="jobLocation"
                name="jobLocation"
                className="form-control"
                placeholder="e.g., New York, NY"
                value={state.jobLocation}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="jobDescription">Job Description (including duties and responsibilities)</label>
          <textarea
            id="jobDescription"
            name="jobDescription"
            className="form-control"
            rows="4"
            placeholder="Job Description"
            value={state.jobDescription}
            onChange={handleChange}
          ></textarea>
        </div>
        <div className="form-group">
          <label htmlFor="qualifications">Required Qualifications & Experience</label>
          <textarea
            id="qualifications"
            name="qualifications"
            className="form-control"
            rows="4"
            placeholder="Required Qualifications & Experience"
            value={state.qualifications}
            onChange={handleChange}
          ></textarea>
        </div>
        <div className="row">
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="salary">Salary (Per Prevailing Wage Determination)</label>
              <input
                type="text"
                id="salary"
                name="salary"
                className="form-control"
                placeholder="e.g., $80,000/year"
                value={state.salary}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="employmentType">Employment Type</label>
              <select
                id="employmentType"
                name="employmentType"
                className="form-control"
                value={state.employmentType}
                onChange={handleChange}
              >
                <option value="">Select Type</option>
                <option value="Full-time, Permanent">Full-time, Permanent</option>
                <option value="Full-time, Temporary">Full-time, Temporary</option>
                <option value="Part-time, Permanent">Part-time, Permanent</option>
                <option value="Part-time, Temporary">Part-time, Temporary</option>
              </select>
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="benefits">Job Benefits (if applicable)</label>
              <input
                type="text"
                id="benefits"
                name="benefits"
                className="form-control"
                placeholder="e.g., Health insurance, 401k"
                value={state.benefits}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
        <div className="text-right">
          <button 
            type="button" 
            className="btn btn-default btn-lg mr-2"
            onClick={prevStep}
          >
            Back
          </button>
          <button 
            type="button" 
            className="btn btn-custom btn-lg"
            onClick={nextStep}
          >
            Next
          </button>
        </div>
      </>
    );
  };

  const renderAdRequirements = () => {
    return (
      <>
        <h3>Advertisement Requirements</h3>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="visaType">Type of Work Visa</label>
              <select
                id="visaType"
                name="visaType"
                className="form-control"
                value={state.visaType}
                onChange={handleChange}
              >
                <option value="">Select Visa Type</option>
                <option value="PERM">PERM</option>
                <option value="H-1B">H-1B</option>
                <option value="H-2B">H-2B</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="wageDetails">Prevailing Wage Determination Details (if available)</label>
              <input
                type="text"
                id="wageDetails"
                name="wageDetails"
                className="form-control"
                placeholder="Prevailing Wage Details"
                value={state.wageDetails}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="adPlacement">Ad Placement Requirement</label>
              <select
                id="adPlacement"
                name="adPlacement"
                className="form-control"
                value={state.adPlacement}
                onChange={handleChange}
              >
                <option value="">Select Placement Type</option>
                <option value="Newspaper">Newspaper</option>
                <option value="Online">Online</option>
                <option value="Radio">Radio</option>
                <option value="Professional Journal">Professional Journal</option>
                <option value="Multiple">Multiple Platforms</option>
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="pubDates">Preferred Publication Dates</label>
              <input
                type="text"
                id="pubDates"
                name="pubDates"
                className="form-control"
                placeholder="e.g., May 1-15, 2025"
                value={state.pubDates}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="pubRegions">Publication Regions (if applicable)</label>
          <input
            type="text"
            id="pubRegions"
            name="pubRegions"
            className="form-control"
            placeholder="e.g., New York metropolitan area"
            value={state.pubRegions}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="specialInstructions">Special Instructions or Compliance Requirements</label>
          <textarea
            id="specialInstructions"
            name="specialInstructions"
            className="form-control"
            rows="4"
            placeholder="Any special instructions or requirements"
            value={state.specialInstructions}
            onChange={handleChange}
          ></textarea>
        </div>
        <div className="text-right">
          <button 
            type="button" 
            className="btn btn-default btn-lg mr-2"
            onClick={prevStep}
          >
            Back
          </button>
          <button 
            type="submit" 
            className="btn btn-custom btn-lg"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit Quote Request"}
          </button>
        </div>
      </>
    );
  };

  return (
    <div id="quote-request">
      <div className="container">
        <div className="section-title">
          <h2>Request a Quote</h2>
          <p>
            Please fill out the form below to request a quote for your PERM recruitment advertising needs.
            We'll get back to you as soon as possible with a customized quote.
          </p>
        </div>
        {successMessage && (
          <div className="alert alert-success">
            {successMessage}
          </div>
        )}
        {errorMessage && (
          <div className="alert alert-danger">
            {errorMessage}
          </div>
        )}
        <div className="row">
          <div className="col-md-12">
            <div className="quote-form-container">
              <div className="step-indicator">
                <div className={`step ${currentStep === 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
                  <span className="step-number">1</span>
                  <p>Client Information</p>
                </div>
                <div className={`step ${currentStep === 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
                  <span className="step-number">2</span>
                  <p>Job Details</p>
                </div>
                <div className={`step ${currentStep === 3 ? 'active' : ''}`}>
                  <span className="step-number">3</span>
                  <p>Ad Requirements</p>
                </div>
              </div>
              <form name="quoteForm" onSubmit={handleSubmit}>
                {renderStep()}
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteRequest;
